<template>
  <v-card dark flat class="subtool-surface">
    <div class="surface-header">
      <div class="title-group">
        <span class="title-icon"><v-icon>mdi-briefcase-outline</v-icon></span>
        <div>
          <div class="surface-eyebrow">Recruiting Content</div>
          <h2>Stellenanzeigen</h2>
          <p>Job-Angebote für die Webseite erstellen und verantwortliche Personen zuweisen.</p>
        </div>
      </div>
      <v-btn color="#009640" depressed class="primary-action" @click="openAddDialog"><v-icon left>mdi-plus</v-icon>Neue Anzeige</v-btn>
    </div>

    <div class="filter-row">
      <v-btn-toggle v-model="showVisible" mandatory dark class="status-toggle">
        <v-btn :value="true" text
          ><v-icon left small>mdi-eye-outline</v-icon>Sichtbar <span>{{ visibleCount }}</span></v-btn
        >
        <v-btn :value="false" text
          ><v-icon left small>mdi-eye-off-outline</v-icon>Versteckt <span>{{ hiddenCount }}</span></v-btn
        >
      </v-btn-toggle>
      <span class="result-pill">{{ filteredJobOffers.length }} Anzeigen</span>
    </div>

    <div v-if="filteredJobOffers.length" class="job-grid">
      <v-card
        v-for="job in filteredJobOffers"
        :key="job._id"
        dark
        flat
        class="job-card"
        role="button"
        tabindex="0"
        @click="openEditDialog(job)"
        @keydown.enter="openEditDialog(job)">
        <div class="job-card-top">
          <span class="job-icon"><v-icon>mdi-briefcase-outline</v-icon></span>
          <span class="state-pill" :class="{ active: job.visible }"
            ><v-icon small>{{ job.visible ? "mdi-eye" : "mdi-eye-off" }}</v-icon
            >{{ job.visible ? "Sichtbar" : "Versteckt" }}</span
          >
        </div>
        <h3>{{ job.title || (job.type && job.type.title) || "Stellenanzeige" }}</h3>
        <div class="job-facts">
          <span><v-icon small>mdi-clock-outline</v-icon>{{ job.durationType || "Kein Anstellungstyp" }}</span>
          <span v-if="job.durationTitle"><v-icon small>mdi-chart-donut</v-icon>{{ job.durationTitle }}</span>
          <span><v-icon small>mdi-account-multiple-outline</v-icon>{{ (job.assignedUsers || []).length }} Zuständige</span>
        </div>
        <div class="card-footer">
          <span>Bearbeitet {{ formatDate(job.updatedAt) }}</span
          ><v-icon small>mdi-arrow-right</v-icon>
        </div>
      </v-card>
    </div>

    <div v-else class="surface-empty">
      <span
        ><v-icon>{{ showVisible ? "mdi-briefcase-search-outline" : "mdi-eye-off-outline" }}</v-icon></span
      >
      <h3>{{ showVisible ? "Keine sichtbaren Anzeigen" : "Keine versteckten Anzeigen" }}</h3>
      <p>{{ showVisible ? "Erstelle eine neue Stellenanzeige für die Webseite." : "Alle Anzeigen sind aktuell sichtbar." }}</p>
      <v-btn v-if="showVisible" outlined color="#58d388" class="secondary-action" @click="openAddDialog"
        ><v-icon left>mdi-plus</v-icon>Anzeige erstellen</v-btn
      >
    </div>

    <v-dialog v-model="dialog" max-width="860px" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly" content-class="website-job-dialog">
      <v-card dark class="editor-card">
        <div class="editor-header">
          <span class="editor-mark"><v-icon>mdi-briefcase-edit-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Stellenanzeige</div>
            <h2>{{ formTitle }}</h2>
            <p>Jobdaten und Bewerbungsempfänger konfigurieren.</p>
          </div>
          <v-btn icon class="icon-action editor-close" aria-label="Dialog schliessen" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="editor-body">
          <v-select
            v-if="!editedItem._id"
            v-model="selectedJobType"
            :items="availableJobTypes"
            item-text="title"
            label="Job-Vorlage auswählen"
            return-object
            outlined
            dark
            class="modern-field"
            hide-details="auto"
            @change="initializeNewJobOffer" />

          <v-form v-if="editedItem.type">
            <section class="editor-section">
              <div class="section-heading">
                <v-icon>mdi-text-box-outline</v-icon>
                <div>
                  <h3>Anzeige</h3>
                  <p>Titel, Pensum und Sichtbarkeit</p>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12"
                  ><v-text-field v-model="editedItem.title" label="Titel der Stellenanzeige" outlined dark class="modern-field" hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-select
                    v-model="editedItem.durationType"
                    :items="['Festanstellung', 'Teilzeit', 'Lehrstelle', 'Praktikum', 'Ausbildung', 'Stundenlohn']"
                    label="Anstellungstyp"
                    outlined
                    dark
                    class="modern-field"
                    hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field
                    v-model="editedItem.durationTitle"
                    label="Pensum, z.B. 50% - 60%"
                    :rules="[percentValidation]"
                    outlined
                    dark
                    class="modern-field"
                    hide-details="auto"
                /></v-col>
                <v-col cols="12"
                  ><v-switch v-model="editedItem.visible" label="Auf der Webseite sichtbar" color="#009640" inset hide-details class="modern-switch"
                /></v-col>
              </v-row>
            </section>

            <section class="editor-section recipients-section">
              <div class="section-heading">
                <v-icon>mdi-account-multiple-outline</v-icon>
                <div>
                  <h3>Bewerbungsempfänger</h3>
                  <p>Diese Personen erhalten neue Bewerbungen per E-Mail und Push-Nachricht.</p>
                </div>
              </div>
              <v-autocomplete
                v-model="userToAdd"
                :items="filteredAvailableUsers"
                :loading="isLoadingUsers"
                :item-text="userDisplayName"
                item-value="_id"
                return-object
                label="Berechtigte Person suchen"
                outlined
                dark
                clearable
                no-data-text="Keine berechtigten Mitarbeiter gefunden"
                class="modern-field"
                hide-details="auto"
                @change="addAssignedUser">
                <template v-slot:item="{ item }">
                  <v-list-item-avatar color="#303033" size="36"
                    ><span class="user-initials">{{ (item.vorname || "U").charAt(0) }}{{ (item.nachname || "").charAt(0) }}</span></v-list-item-avatar
                  >
                  <v-list-item-content
                    ><v-list-item-title>{{ item.vorname }} {{ item.nachname }}</v-list-item-title
                    ><v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle></v-list-item-content
                  >
                </template>
              </v-autocomplete>

              <div v-if="editedItem.assignedUsers.length" class="recipient-list">
                <div v-for="(user, index) in editedItem.assignedUsers" :key="user._id || index" class="recipient-row">
                  <span class="recipient-avatar">{{ (user.vorname || "U").charAt(0) }}{{ (user.nachname || "").charAt(0) }}</span>
                  <div>
                    <strong>{{ user.vorname }} {{ user.nachname }}</strong
                    ><span>{{ user.email }}</span>
                  </div>
                  <v-btn icon class="remove-action" :aria-label="`${user.vorname || 'Person'} entfernen`" @click="removeAssignedUser(index)"
                    ><v-icon small>mdi-delete-outline</v-icon></v-btn
                  >
                </div>
              </div>
              <v-alert v-else type="warning" text class="recipient-alert">Noch keine zuständige Person zugewiesen.</v-alert>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="editor-actions">
          <v-spacer />
          <v-btn text class="cancel-action" @click="closeDialog">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" :disabled="!isJobOfferValid" :loading="isSaving" @click="saveJobOffer"
            ><v-icon left>mdi-content-save-outline</v-icon>Speichern</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  data() {
    return {
      jobOffers: [],
      availableJobTypes: [],
      selectedJobType: null,
      dialog: false,
      isSaving: false,
      availableUsers: [],
      isLoadingUsers: false,
      userToAdd: null,
      editedItem: {},
      showVisible: true,
      defaultItem: {
        _id: null,
        type: null,
        title: "",
        visible: false,
        durationType: "",
        durationTitle: "",
        assignedUsers: [],
        updatedAt: new Date().toISOString(),
      },
      percentValidation: (value) => {
        if (!value) return true
        const regex = /^(\d{1,3}%|\d{1,3}%\s*-\s*\d{1,3}%)$/
        return regex.test(value) || 'Format: "50%" oder "50% - 60%"'
      },
    }
  },
  computed: {
    filteredAvailableUsers() {
      const assignedIds = (this.editedItem.assignedUsers || []).map((user) => user._id || user)
      return this.availableUsers.filter((user) => !assignedIds.includes(user._id))
    },
    filteredJobOffers() {
      return this.jobOffers.filter((job) => Boolean(job.visible) === this.showVisible)
    },
    visibleCount() {
      return this.jobOffers.filter((job) => job.visible).length
    },
    hiddenCount() {
      return this.jobOffers.length - this.visibleCount
    },
    formTitle() {
      return this.editedItem._id ? "Stellenanzeige bearbeiten" : "Neue Stellenanzeige erstellen"
    },
    isJobOfferValid() {
      if (!this.editedItem.type) return false
      const regex = /^(\d{1,3}%|\d{1,3}%\s*-\s*\d{1,3}%)$/
      return regex.test(this.editedItem.durationTitle) || this.editedItem.durationTitle === ""
    },
  },
  async created() {
    await this.loadEligibleUsers()
    await this.init()
  },
  methods: {
    async loadEligibleUsers() {
      this.isLoadingUsers = true
      try {
        this.availableUsers = await api.jobApplications.fetchEligibleUsers()
      } catch (error) {
        if (this.$toast) this.$toast.error("Berechtigte Mitarbeiter konnten nicht geladen werden")
      } finally {
        this.isLoadingUsers = false
      }
    },
    userDisplayName(user) {
      if (!user) return ""
      return `${user.vorname || ""} ${user.nachname || ""} (${user.email || ""})`.trim()
    },
    addAssignedUser(user) {
      if (!user) return
      if (!this.editedItem.assignedUsers) this.$set(this.editedItem, "assignedUsers", [])
      const alreadyAdded = this.editedItem.assignedUsers.some((item) => (item._id || item) === user._id)
      if (!alreadyAdded) this.editedItem.assignedUsers.push(user)
      this.$nextTick(() => {
        this.userToAdd = null
      })
    },
    async init() {
      try {
        this.jobOffers = await api.fetchJobOffers()
        this.availableJobTypes = await api.fetchJobTypes()
      } catch (error) {
        if (this.$toast) this.$toast.error("Fehler beim Laden der Daten")
      }
    },
    openAddDialog() {
      this.selectedJobType = null
      this.editedItem = { ...this.defaultItem, assignedUsers: [] }
      this.userToAdd = null
      this.dialog = true
    },
    openEditDialog(item) {
      this.editedItem = JSON.parse(JSON.stringify(item))
      if (typeof this.editedItem.type === "string") {
        const jobType = this.availableJobTypes.find((type) => type._id === this.editedItem.type)
        this.editedItem.type = jobType || { _id: this.editedItem.type, title: this.editedItem.title }
      }
      if (!this.editedItem.assignedUsers) {
        this.$set(this.editedItem, "assignedUsers", [])
      } else if (this.editedItem.assignedUsers.length > 0 && typeof this.editedItem.assignedUsers[0] === "string") {
        const populated = this.editedItem.assignedUsers.map((id) => this.availableUsers.find((user) => user._id === id)).filter(Boolean)
        this.$set(this.editedItem, "assignedUsers", populated)
      }
      this.userToAdd = null
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
    },
    initializeNewJobOffer() {
      if (!this.selectedJobType) return
      this.editedItem = { ...this.defaultItem, type: this.selectedJobType, title: this.selectedJobType.title, assignedUsers: [] }
    },
    removeAssignedUser(index) {
      this.editedItem.assignedUsers.splice(index, 1)
    },
    async saveJobOffer() {
      if (!this.isJobOfferValid) return
      this.isSaving = true
      const payload = {
        type: this.editedItem.type._id || this.editedItem.type,
        title: this.editedItem.title,
        visible: this.editedItem.visible,
        durationType: this.editedItem.durationType,
        durationTitle: this.editedItem.durationTitle,
        assignedUsers: this.editedItem.assignedUsers.map((user) => user._id || user),
      }
      try {
        if (this.editedItem._id) await api.updateJobOffer(this.editedItem._id, payload)
        else await api.createJobOffer(payload)
        if (this.$toast) this.$toast.success("Job-Anzeige erfolgreich gespeichert")
        this.closeDialog()
        this.init()
      } catch (error) {
        if (this.$toast) this.$toast.error("Fehler beim Speichern der Job-Anzeige")
      } finally {
        this.isSaving = false
      }
    },
    formatDate(date) {
      return this.$moment(date).format("DD.MM.YY HH:mm") + " Uhr"
    },
  },
}
</script>

<style scoped>
.subtool-surface {
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
  font-size: 1rem;
}

.surface-header,
.title-group,
.filter-row,
.job-card-top,
.card-footer,
.state-pill,
.editor-header,
.section-heading,
.recipient-row {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1rem;
}

.title-group {
  min-width: 0;
  gap: 0.75rem;
}

.title-icon,
.job-icon,
.editor-mark,
.recipient-avatar,
.surface-empty > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.title-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.title-icon .v-icon,
.job-icon .v-icon,
.editor-mark .v-icon,
.surface-empty > span .v-icon {
  color: currentColor;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.editor-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.editor-header p,
.section-heading p,
.surface-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.filter-row {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.9375rem;
  background: rgba(0, 0, 0, 0.12);
}

.status-toggle {
  border-radius: 0.75rem !important;
  background: transparent !important;
}

.status-toggle .v-btn {
  min-height: 2.5rem;
  border-radius: 0.625rem !important;
  color: var(--muted) !important;
  font-size: 0.875rem;
  letter-spacing: 0;
  text-transform: none;
}

.status-toggle .v-btn--active {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.13) !important;
}

.status-toggle .v-btn span {
  margin-left: 0.4375rem;
  color: #58d388;
  font-weight: 800;
}

.result-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 750;
  white-space: nowrap;
}

.job-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.job-card {
  display: flex;
  flex-direction: column;
  min-height: 15rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.0625rem !important;
  outline: none;
  background: rgba(255, 255, 255, 0.035) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.job-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.job-card-top,
.card-footer {
  justify-content: space-between;
  gap: 0.75rem;
}

.job-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.state-pill {
  min-height: 1.625rem;
  gap: 0.25rem;
  padding: 0.1875rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.75rem;
  font-weight: 700;
}

.state-pill.active {
  border-color: rgba(0, 150, 64, 0.28);
  color: #58d388;
  background: rgba(0, 150, 64, 0.1);
}

.state-pill .v-icon {
  color: currentColor;
}

.job-card h3,
.surface-empty h3,
.section-heading h3 {
  margin: 1rem 0 0;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
}

.job-facts {
  display: grid;
  gap: 0.5rem;
  margin: 1rem 0;
}

.job-facts span {
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.375rem;
  color: #c7c7cb;
  font-size: 0.875rem;
}

.job-facts .v-icon {
  color: #8f9095;
}

.card-footer {
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  color: var(--muted);
  font-size: 0.75rem;
}

.card-footer .v-icon {
  color: #58d388;
}

.surface-empty {
  display: flex;
  min-height: 16rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.surface-empty > span {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.75rem;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.surface-empty h3 {
  margin-top: 0;
}

.surface-empty .secondary-action {
  margin-top: 1rem;
  color: #58d388 !important;
}

.editor-card {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  color: var(--text);
  background: rgba(28, 28, 29, 0.985) !important;
  font-size: 1rem;
}

.editor-header {
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.editor-mark {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.editor-header > div {
  min-width: 0;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.editor-close {
  margin-left: auto;
}

.editor-body {
  min-height: 0;
  flex: 1 1 auto;
  max-height: none;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-section {
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.0625rem;
  background: rgba(255, 255, 255, 0.028);
}

.recipients-section {
  margin-top: 0.875rem;
}

.section-heading {
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 1rem;
}

.section-heading h3 {
  margin-top: 0;
}

.section-heading > .v-icon {
  color: #58d388;
}

.modern-field {
  margin-bottom: 0.75rem;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.modern-switch ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.recipient-list {
  display: grid;
  gap: 0.5rem;
}

.recipient-row {
  min-width: 0;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12);
}

.recipient-avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.6875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
  font-size: 0.75rem;
  font-weight: 800;
}

.recipient-row > div {
  display: grid;
  min-width: 0;
}

.recipient-row strong {
  overflow: hidden;
  color: var(--text);
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recipient-row > div span {
  overflow: hidden;
  color: var(--muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-action {
  margin-left: auto;
  color: #f87171 !important;
}

.recipient-alert {
  margin: 0;
  border-radius: 0.8125rem;
  font-size: 0.875rem;
}

.user-initials {
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 800;
}

.editor-actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.cancel-action {
  min-height: 2.75rem;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .job-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: rgba(255, 255, 255, 0.055) !important;
  }
}

@media (max-width: 59.9375rem) {
  .job-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .subtool-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header,
  .filter-row {
    align-items: stretch;
    flex-direction: column;
  }

  .surface-header .primary-action {
    width: 100%;
  }

  .status-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .job-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .editor-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .editor-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .editor-mark,
  .editor-header p {
    display: none;
  }

  .editor-body {
    max-height: none;
    padding: 0.75rem !important;
  }

  .editor-actions {
    flex-wrap: wrap;
    padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important;
  }

  .editor-actions .primary-action {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
